import { Ref, useImperativeHandle, useMemo } from 'react';

import { useAuthentication, User } from '@ravago/shared/authentication';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { PhoneInputField } from '@ravago/shared/radiance/components/input-field/components/PhoneInputField/PhoneInputField';
import {
   Countries,
   CountryCode,
   PhoneDetails
} from '@ravago/shared/radiance/components/input-field/components/PhoneInputField/PhoneInputField.types';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { isValidPhoneNumber } from 'libphonenumber-js';
import { Controller, useForm } from 'react-hook-form';

import { useConfigContext } from '../../../../../../../hooks/use-config-context';
import { usePathCountryCode, usePathLanguageCode } from '../../../../../../../hooks/use-path-locale';
import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';
import { RfqImage } from '../fields/rfq-image';
import { RfqContactDetailsIllustration } from '../images/rfq-contact-details-illustration';

export interface FormValue {
   email: string;
   firstName: string;
   lastName: string;
   companyName: string;
   country: DropdownOption;
   phone: PhoneDetails;
   acceptTermsAndConditions: boolean;
}

export interface LocalFormValue {
   email: string;
   firstName: string;
   lastName: string;
   companyName: string;
   country: DropdownOption | undefined;
   phone: PhoneDetails | undefined;
   acceptTermsAndConditions: boolean;
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   countries: { fullName: string; shortName: string }[] | undefined;
   email: string | undefined;
   defaultFormValue: FormValue | undefined;
   onSubmit: (value: FormValue) => void;
}

const getDefaultFormValue = (user: User | undefined, email: string | undefined, defaultCountry: string, countryOptions: DropdownOption[]) => {
   const defaultCountryOption = countryOptions.find((countryOption) => countryOption.id === defaultCountry);
   const defaultPhoneCountry = defaultCountryOption ? Countries[defaultCountryOption.id as CountryCode] : undefined;
   const defaultPhone = defaultPhoneCountry ? { phone: '', countryInfo: defaultPhoneCountry } : undefined;

   return {
      email: user?.mail ?? email ?? '',
      firstName: user?.firstname ?? '',
      lastName: user?.lastname ?? '',
      companyName: '',
      country: defaultCountryOption,
      phone: defaultPhone,
      acceptTermsAndConditions: false
   };
};

export const RfqFlyoutStepContactDetails = ({ stepRef, countries, email, defaultFormValue, onSubmit }: Props) => {
   const languageCode = usePathLanguageCode();
   const countryCode = usePathCountryCode();
   const translate = useTranslations();
   const { hostName } = useConfigContext();
   const { user } = useAuthentication();

   const countryOptions: DropdownOption[] = useMemo(
      () =>
         (countries ?? [])
            .map((country) => ({
               id: country.shortName,
               value: country.fullName
            }))
            .sort((a, b) => a.value.localeCompare(b.value)),
      [countries]
   );

   const { control, handleSubmit, setValue, getValues } = useForm<LocalFormValue, unknown, FormValue>({
      defaultValues: defaultFormValue ?? getDefaultFormValue(user, email, countryCode, countryOptions)
   });

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   const onCountryChanged = (newValue: DropdownOption | undefined) => {
      if (newValue) {
         const phone = getValues('phone');
         const countryInfo = Countries[newValue.id as CountryCode];
         setValue('phone', { phone: phone?.phone ?? '', countryInfo });
      }
   };

   return (
      <Box direction="column">
         <RfqImage>
            <RfqContactDetailsIllustration />
         </RfqImage>
         <Typography component="span" variant="body" color="strong">
            {translate('page-consumer.request-for-quotation.contact-info.instructions')}
         </Typography>
         <Box direction="column">
            <InputField
               disabled
               type="email"
               label={translate('page-consumer.request-for-quotation.contact-info.email-address')}
               id="rfq-contact-details-email-address"
               value={user ? user.mail : email}
            />

            <Box gap="xs">
               <Controller
                  control={control}
                  name="firstName"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.first-name') }),
                     maxLength: {
                        value: 40,
                        message: translate('form-validation.max-length', {
                           field: translate('form-field.first-name'),
                           value: 40
                        })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        disabled={!!user}
                        label={translate('page-consumer.request-for-quotation.contact-info.first-name')}
                        id="rfq-contact-details-first-name"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />

               <Controller
                  control={control}
                  name="lastName"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.last-name') }),
                     maxLength: {
                        value: 80,
                        message: translate('form-validation.max-length', {
                           field: translate('form-field.last-name'),
                           value: 80
                        })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        disabled={!!user}
                        label={translate('page-consumer.request-for-quotation.contact-info.last-name')}
                        id="rfq-contact-details-last-name"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </Box>
            <Controller
               control={control}
               name="companyName"
               rules={{
                  required: translate('form-validation.required', { field: translate('form-field.company-name') }),
                  maxLength: {
                     value: 40,
                     message: translate('form-validation.max-length', {
                        field: translate('form-field.company-name'),
                        value: 40
                     })
                  }
               }}
               render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <InputField
                     inputRef={ref}
                     {...field}
                     label={translate('page-consumer.request-for-quotation.contact-info.company-name')}
                     id="rfq-contact-details-company-name"
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />
            <Controller
               control={control}
               name="country"
               rules={{
                  required: translate('form-validation.required', { field: translate('form-field.country') })
               }}
               render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                  <Dropdown
                     inputRef={ref}
                     {...field}
                     onChange={(e) => {
                        onChange(e);
                        onCountryChanged(e);
                     }}
                     id="rfq-contact-dropdown-country"
                     label={translate('page-consumer.request-for-quotation.contact-info.country')}
                     leadingIcon=""
                     emptyOption={false}
                     options={countryOptions}
                     disabled={!countries}
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />
            <Controller
               control={control}
               name="phone"
               rules={{
                  validate: {
                     valid: (value) =>
                        value && isValidPhoneNumber(`+${value.countryInfo?.prefix}${value.phone}`)
                           ? undefined
                           : translate('request-for-quotation.invalid-phone-number')
                  }
               }}
               render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <PhoneInputField
                     inputRef={ref}
                     {...field}
                     autocomplete="on"
                     countryFilter={[]}
                     filterType="include"
                     id="rfq-contact-details-phone"
                     label={translate('page-consumer.request-for-quotation.contact-info.phone')}
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />
         </Box>
         <Box items="center" gap="2xs" top="-15px" left="-5px" position="relative">
            <Controller
               control={control}
               name="acceptTermsAndConditions"
               rules={{
                  validate: (currentValue) => currentValue === true
               }}
               render={({ field: { ref, value, ...field }, fieldState: { error } }) => (
                  <Checkbox inputRef={ref} checked={value} {...field} id="checkbox-flyout-terms" error={!!error}>
                     <Typography variant="body" component="span">
                        {translate('page-consumer.request-for-quotation.contact-info.accept-terms')}
                     </Typography>
                     <span>&nbsp;</span>
                     <Link underline="always" color="primary" href={`${hostName}/my/${String(languageCode)}/legal/terms-of-use`} target="_blank">
                        <Typography variant="body" component="span" color="primary">
                           {translate('page-consumer.request-for-quotation.contact-info.terms-conditions')}
                        </Typography>
                     </Link>
                  </Checkbox>
               )}
            />
         </Box>
      </Box>
   );
};
